/**
 * Calorimetry (mixing), latent heat and the water heating curve.
 * Shares units, reading and failure types with `heat.ts`.
 */

import {
  ENERGY_UNITS,
  HEAT_MASS_UNITS,
  type HeatFailure,
  LATENT_HEAT_UNITS,
  readAll,
  SPECIFIC_HEAT_UNITS,
  unitFactor,
} from "./heat";
import { findLatentHeat, findSpecificHeat } from "./heat-data";
import { fromKelvin } from "./units";

/* --------------------------------------------------------- calorimetry */

export type MixKey = "m1" | "c1" | "t1" | "m2" | "c2" | "t2" | "tf";
export const MIX_KEYS: readonly MixKey[] = [
  "m1",
  "c1",
  "t1",
  "m2",
  "c2",
  "t2",
  "tf",
];

export interface MixInput {
  /** Final temperature, or the specific heat of the first object. */
  unknown: "tf" | "c1";
  text: Record<MixKey, string> & { cal: string };
  unit: { m: string; c: string; t: string; cal: string };
}

export interface MixSolution {
  ok: true;
  unknown: "tf" | "c1";
  /** g, J/(g·°C), K */
  values: Record<MixKey, number>;
  /** Calorimeter heat capacity, J/°C. */
  cal: number;
  /** Heat gained by each body, J: negative for the one that cools. */
  q1: number;
  q2: number;
  qCal: number;
  answer: number;
  answerUnit: string;
}

export type MixOutcome = MixSolution | HeatFailure;

const MIX_KINDS: Record<MixKey, "positive" | "temperature"> = {
  m1: "positive",
  c1: "positive",
  t1: "temperature",
  m2: "positive",
  c2: "positive",
  t2: "temperature",
  tf: "temperature",
};

export function solveMix(input: MixInput): MixOutcome {
  const { unknown, unit } = input;
  const factorOf = (key: MixKey): number => {
    if (key.startsWith("m")) {
      return unitFactor(HEAT_MASS_UNITS, unit.m);
    }
    if (key.startsWith("c")) {
      return unitFactor(SPECIFIC_HEAT_UNITS, unit.c);
    }
    return 1;
  };
  const wanted = MIX_KEYS.filter((key) =>
    unknown === "tf" ? key !== "tf" : key !== "c1"
  );
  const read = readAll(
    wanted.map((key) => ({
      key,
      text: input.text[key],
      kind: MIX_KINDS[key],
      unit: key.startsWith("t") ? unit.t : "",
      factor: factorOf(key),
    }))
  );
  if ("ok" in read) {
    return read;
  }
  const calText = input.text.cal.trim() === "" ? "0" : input.text.cal;
  const calRead = readAll([
    {
      key: "cal",
      text: calText,
      kind: "any",
      unit: "",
      factor: unitFactor(ENERGY_UNITS, unit.cal),
    },
  ]);
  if ("ok" in calRead) {
    return calRead;
  }
  const cal = calRead.values.cal;
  if (cal < 0) {
    return { ok: false, reason: "nonPositive", key: "cal" };
  }
  const v = read.values;
  const sink = v.m2 * v.c2 + cal;
  let value: number;
  if (unknown === "tf") {
    const source = v.m1 * v.c1;
    value = (source * v.t1 + sink * v.t2) / (source + sink);
  } else {
    const between = (v.tf - v.t1) * (v.tf - v.t2) < 0;
    if (!between) {
      return { ok: false, reason: "outsideRange" };
    }
    value = (-sink * (v.tf - v.t2)) / (v.m1 * (v.tf - v.t1));
  }
  const full = { ...v, [unknown]: value } as Record<MixKey, number>;
  return {
    ok: true,
    unknown,
    values: full,
    cal,
    q1: full.m1 * full.c1 * (full.tf - full.t1),
    q2: full.m2 * full.c2 * (full.tf - full.t2),
    qCal: cal * (full.tf - full.t2),
    answer:
      unknown === "tf"
        ? fromKelvin(value, unit.t)
        : value / unitFactor(SPECIFIC_HEAT_UNITS, unit.c),
    answerUnit: unknown === "tf" ? unit.t : unit.c,
  };
}

/* --------------------------------------------------------- latent heat */

export type LatentProcess = "melt" | "freeze" | "boil" | "condense";
export const LATENT_PROCESSES: readonly LatentProcess[] = [
  "melt",
  "freeze",
  "boil",
  "condense",
];

/** Heat flows in for melting and boiling, out for freezing and condensing. */
export function processSign(process: LatentProcess): 1 | -1 {
  return process === "melt" || process === "boil" ? 1 : -1;
}

export function processUsesFusion(process: LatentProcess): boolean {
  return process === "melt" || process === "freeze";
}

/** The table's latent heat for a substance and process, J/g. */
export function tableLatentHeat(
  substance: string,
  process: LatentProcess
): number | null {
  const entry = findLatentHeat(substance);
  if (!entry) {
    return null;
  }
  return processUsesFusion(process) ? entry.fusion : entry.vaporization;
}

/** Normal melting or boiling point of a table substance, °C. */
export function tableTransitionPoint(
  substance: string,
  process: LatentProcess
): number | null {
  const entry = findLatentHeat(substance);
  if (!entry) {
    return null;
  }
  return processUsesFusion(process) ? entry.meltingPoint : entry.boilingPoint;
}

export type LatentTerm = "q" | "m" | "l";

export interface LatentInput {
  unknown: LatentTerm;
  process: LatentProcess;
  text: Record<LatentTerm, string>;
  unit: { q: string; m: string; l: string };
}

export interface LatentSolution {
  ok: true;
  unknown: LatentTerm;
  process: LatentProcess;
  /** Magnitudes: J, g, J/g. */
  values: Record<LatentTerm, number>;
  /** Signed heat, J: positive when absorbed. */
  signedQ: number;
  answer: number;
  answerUnit: string;
}

export type LatentOutcome = LatentSolution | HeatFailure;

export function solveLatent(input: LatentInput): LatentOutcome {
  const { unknown, unit } = input;
  const factors: Record<LatentTerm, number> = {
    q: unitFactor(ENERGY_UNITS, unit.q),
    m: unitFactor(HEAT_MASS_UNITS, unit.m),
    l: unitFactor(LATENT_HEAT_UNITS, unit.l),
  };
  const read = readAll(
    (["q", "m", "l"] as const)
      .filter((key) => key !== unknown)
      .map((key) => ({
        key,
        text: input.text[key],
        kind: "positive" as const,
        unit: "",
        factor: factors[key],
      }))
  );
  if ("ok" in read) {
    return read;
  }
  const v = read.values;
  let value: number;
  if (unknown === "q") {
    value = v.m * v.l;
  } else if (unknown === "m") {
    value = v.q / v.l;
  } else {
    value = v.q / v.m;
  }
  const full = { ...v, [unknown]: value } as Record<LatentTerm, number>;
  const sign = processSign(input.process);
  return {
    ok: true,
    unknown,
    process: input.process,
    values: full,
    signedQ: sign * full.q,
    answer:
      unknown === "q" ? (sign * value) / factors.q : value / factors[unknown],
    answerUnit: unit[unknown],
  };
}

/* ------------------------------------------------- water heating curve */

export type WaterPhase = "ice" | "water" | "steam";
const PHASE_ORDER: readonly WaterPhase[] = ["ice", "water", "steam"];
const PHASE_TOLERANCE = 1e-9;
const WATER_FUSION_C = 0;
const WATER_BOILING_C = 100;

export interface CurveInput {
  mass: string;
  massUnit: string;
  start: { text: string; phase: WaterPhase };
  end: { text: string; phase: WaterPhase };
  tempUnit: string;
}

export type CurveKind = "warm" | "melt" | "freeze" | "boil" | "condense";

export interface CurveSegment {
  kind: CurveKind;
  phase: WaterPhase;
  /** Temperatures in °C, in the direction of travel. */
  from: number;
  to: number;
  /** J/(g·°C) for warming, J/g for transitions. */
  rate: number;
  /** Signed heat, J. */
  q: number;
}

export interface CurveSolution {
  ok: true;
  /** g */
  grams: number;
  segments: CurveSegment[];
  total: number;
}

export type CurveOutcome = CurveSolution | HeatFailure;

const WATER_SPECIFIC_HEAT_IDS: Record<WaterPhase, string> = {
  ice: "ice",
  water: "water",
  steam: "steam",
};

function waterSpecificHeat(phase: WaterPhase): number {
  return findSpecificHeat(WATER_SPECIFIC_HEAT_IDS[phase])?.value ?? 0;
}

function phaseFits(phase: WaterPhase, celsius: number): boolean {
  if (phase === "ice") {
    return celsius <= WATER_FUSION_C + PHASE_TOLERANCE;
  }
  if (phase === "steam") {
    return celsius >= WATER_BOILING_C - PHASE_TOLERANCE;
  }
  return (
    celsius >= WATER_FUSION_C - PHASE_TOLERANCE &&
    celsius <= WATER_BOILING_C + PHASE_TOLERANCE
  );
}

/** Segments for warming from one phase and temperature to a higher phase. */
function warmingSegments(
  grams: number,
  from: { phase: WaterPhase; c: number },
  to: { phase: WaterPhase; c: number }
): CurveSegment[] {
  const fusion = findLatentHeat("water")?.fusion ?? 0;
  const vaporization = findLatentHeat("water")?.vaporization ?? 0;
  const a = PHASE_ORDER.indexOf(from.phase);
  const b = PHASE_ORDER.indexOf(to.phase);
  const out: CurveSegment[] = [];
  const bounds = [WATER_FUSION_C, WATER_BOILING_C];
  for (let index = a; index <= b; index += 1) {
    const phase = PHASE_ORDER[index];
    const start = index === a ? from.c : bounds[index - 1];
    const end = index === b ? to.c : bounds[index];
    const rate = waterSpecificHeat(phase);
    if (end !== start) {
      out.push({
        kind: "warm",
        phase,
        from: start,
        to: end,
        rate,
        q: grams * rate * (end - start),
      });
    }
    if (index < b) {
      const latent = index === 0 ? fusion : vaporization;
      out.push({
        kind: index === 0 ? "melt" : "boil",
        phase: PHASE_ORDER[index + 1],
        from: bounds[index],
        to: bounds[index],
        rate: latent,
        q: grams * latent,
      });
    }
  }
  return out;
}

const REVERSED: Record<CurveKind, CurveKind> = {
  warm: "warm",
  melt: "freeze",
  boil: "condense",
  freeze: "melt",
  condense: "boil",
};

export function waterHeatingCurve(input: CurveInput): CurveOutcome {
  const read = readAll([
    {
      key: "mass",
      text: input.mass,
      kind: "positive",
      unit: "",
      factor: unitFactor(HEAT_MASS_UNITS, input.massUnit),
    },
    {
      key: "start",
      text: input.start.text,
      kind: "temperature",
      unit: input.tempUnit,
      factor: 1,
    },
    {
      key: "end",
      text: input.end.text,
      kind: "temperature",
      unit: input.tempUnit,
      factor: 1,
    },
  ]);
  if ("ok" in read) {
    return read;
  }
  const start = fromKelvin(read.values.start, "C");
  const end = fromKelvin(read.values.end, "C");
  if (!phaseFits(input.start.phase, start)) {
    return { ok: false, reason: "phaseMismatch", key: "start" };
  }
  if (!phaseFits(input.end.phase, end)) {
    return { ok: false, reason: "phaseMismatch", key: "end" };
  }
  const grams = read.values.mass;
  const a = PHASE_ORDER.indexOf(input.start.phase);
  const b = PHASE_ORDER.indexOf(input.end.phase);
  let segments: CurveSegment[];
  if (a <= b) {
    segments = warmingSegments(
      grams,
      { phase: input.start.phase, c: start },
      { phase: input.end.phase, c: end }
    );
  } else {
    // Cooling is the same path walked backwards, with every heat flow reversed.
    segments = warmingSegments(
      grams,
      { phase: input.end.phase, c: end },
      { phase: input.start.phase, c: start }
    )
      .reverse()
      .map((seg) => ({
        ...seg,
        kind: REVERSED[seg.kind],
        phase:
          seg.kind === "warm"
            ? seg.phase
            : PHASE_ORDER[PHASE_ORDER.indexOf(seg.phase) - 1],
        from: seg.to,
        to: seg.from,
        q: -seg.q,
      }));
  }
  const total = segments.reduce((sum, seg) => sum + seg.q, 0);
  return { ok: true, grams, segments, total };
}
